import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import CustomLinearProgress from "app/components/common/CustomLinearProgress";
import BulkFileUploadArea from "app/modules/student/components/BulkAdmission/components/BulkFileUploadArea";
import {
  bulkUpdateStudentFieldsByExcel,
  verifySupportBulkStudentUpdateAccess,
} from "app/services/schoolService/studentBulk.service";
import { selectActiveSession } from "app/modules/schools/selectors";
import { useSelector } from "react-redux";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import {
  SUPPORT_ACCESS_PIN_STORAGE_KEY,
  SUPPORT_ACCESS_STORAGE_KEY,
  SUPPORT_BULK_UPDATE_FIELDS,
} from "./constants";
import {
  downloadSupportBulkUpdateSample,
  readFileAsArrayBuffer,
  validateSupportBulkUpdateFile,
} from "./supportBulkUpdateUtils";

const ResultsPanel = ({ validationResult, uploadResponse }) => {
  const hasValidationErrors =
    validationResult?.fileErrors?.length > 0 ||
    validationResult?.rowErrors?.length > 0;
  const hasUploadResults =
    uploadResponse?.successList?.length > 0 ||
    uploadResponse?.failedList?.length > 0;

  if (!hasValidationErrors && !hasUploadResults) {
    return (
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          height: "100%",
          minHeight: 420,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#FAFBFC",
          border: "1px dashed #B6C8D6",
        }}
      >
        <Typography color="text.secondary" textAlign="center">
          Validation errors, success and failed results will appear here after
          file upload and submit.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      {validationResult?.fileErrors?.length > 0 && (
        <Paper
          sx={{ p: 2, borderRadius: 3, border: "1px solid #FCA5A5", flex: "0 0 auto" }}
        >
          <Typography variant="h6" sx={{ mb: 1 }} color="error">
            File Validation Errors
          </Typography>
          <Stack spacing={0.5}>
            {validationResult.fileErrors.map((error) => (
              <Typography key={error} color="error" variant="body2">
                {error}
              </Typography>
            ))}
          </Stack>
        </Paper>
      )}

      {validationResult?.rowErrors?.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 3, flex: "1 1 auto", overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 1 }} color="error">
            Row Validation Errors ({validationResult.rowErrors.length})
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Fix these errors and re-upload the file before submitting.
          </Typography>
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Adm No</TableCell>
                  <TableCell>Errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validationResult.rowErrors.map((item) => (
                  <TableRow
                    key={`validation-${item.rowIndex}-${item.admissionNumber}`}
                  >
                    <TableCell>{item.rowIndex}</TableCell>
                    <TableCell>{item.admissionNumber ?? "-"}</TableCell>
                    <TableCell>{item.errors.join("; ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {uploadResponse?.successList?.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 3, flex: "1 1 auto", overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 1 }} color="success.main">
            Success ({uploadResponse.successCount})
          </Typography>
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Adm No</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadResponse.successList.map((item) => (
                  <TableRow key={`success-${item.rowIndex}-${item.admissionNumber}`}>
                    <TableCell>{item.rowIndex}</TableCell>
                    <TableCell>{item.admissionNumber}</TableCell>
                    <TableCell>{item.studentName}</TableCell>
                    <TableCell>{item.updatedFields?.join(", ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {uploadResponse?.failedList?.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 3, flex: "1 1 auto", overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 1 }} color="error">
            Failed ({uploadResponse.failedCount})
          </Typography>
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Adm No</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadResponse.failedList.map((item) => (
                  <TableRow key={`failed-${item.rowIndex}-${item.admissionNumber}`}>
                    <TableCell>{item.rowIndex}</TableCell>
                    <TableCell>{item.admissionNumber ?? "-"}</TableCell>
                    <TableCell>{item.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
};

const SupportBulkStudentUpdate = () => {
  const activeSession = useSelector(selectActiveSession);
  const [isAccessGranted, setIsAccessGranted] = useState(
    () => sessionStorage.getItem(SUPPORT_ACCESS_STORAGE_KEY) === "true"
  );
  const [accessPin, setAccessPin] = useState(
    () => sessionStorage.getItem(SUPPORT_ACCESS_PIN_STORAGE_KEY) || ""
  );
  const [session, setSession] = useState(activeSession || "");
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const { mutate: verifyAccessMutate, isLoading: verifyAccessLoading } =
    useMutation(verifySupportBulkStudentUpdateAccess, {
      onSuccess: () => {
        sessionStorage.setItem(SUPPORT_ACCESS_STORAGE_KEY, "true");
        sessionStorage.setItem(SUPPORT_ACCESS_PIN_STORAGE_KEY, accessPin);
        setIsAccessGranted(true);
        toast.success("Access granted");
      },
      onError: ({ response }) => {
        toast.error(response?.data?.message || "Invalid access pin");
      },
    });

  const resetUploadState = useCallback(() => {
    setSelectedFile(null);
    setValidationResult(null);
    setUploadResponse(null);
  }, []);

  const handleVerifyAccess = () => {
    if (accessPin.length !== 4) {
      toast.warn("Please enter a 4-digit access pin");
      return;
    }
    verifyAccessMutate({ accessPin });
  };

  const toggleField = useCallback((fieldKey) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((key) => key !== fieldKey)
        : [...prev, fieldKey]
    );
    resetUploadState();
  }, [resetUploadState]);

  const handleDownloadSample = useCallback(() => {
    if (!selectedFields.length) {
      toast.warn("Please select at least one field to update");
      return;
    }
    downloadSupportBulkUpdateSample(selectedFields);
    toast.success("Sample file downloaded");
  }, [selectedFields]);

  const isSetupReady = Boolean(session?.trim()) && selectedFields.length > 0;

  const handleFileSelect = useCallback(
    async (file) => {
      if (!isSetupReady) {
        toast.warn("Please enter session and select update fields first");
        return;
      }

      setIsValidating(true);
      setUploadResponse(null);

      try {
        const fileBuffer = await readFileAsArrayBuffer(file);
        const validation = validateSupportBulkUpdateFile(
          fileBuffer,
          selectedFields
        );
        setSelectedFile(file);
        setValidationResult(validation);

        if (!validation.isValid) {
          if (validation.fileErrors.length) {
            toast.error(validation.fileErrors[0]);
          } else {
            toast.error(
              `${validation.rowErrors.length} row(s) have validation errors`
            );
          }
          return;
        }

        toast.success(
          `${validation.validRowCount} row(s) validated. Click Submit to update.`
        );
      } catch (error) {
        resetUploadState();
        toast.error(error?.message || "Failed to read file");
      } finally {
        setIsValidating(false);
      }
    },
    [isSetupReady, resetUploadState, selectedFields]
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      toast.warn("Please upload a file first");
      return;
    }

    if (!validationResult?.isValid) {
      toast.warn("Please fix validation errors before submitting");
      return;
    }

    const storedPin =
      accessPin || sessionStorage.getItem(SUPPORT_ACCESS_PIN_STORAGE_KEY);

    if (!storedPin) {
      toast.warn("Please verify access pin first");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("session", session.trim());
    formData.append("fields", JSON.stringify(selectedFields));
    formData.append("accessPin", storedPin);

    setIsUploading(true);
    setUploadResponse(null);

    try {
      const response = await bulkUpdateStudentFieldsByExcel(formData);
      setUploadResponse(response?.data);
      setValidationResult(null);
      toast.success(response?.data?.message || "Upload processed");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Upload failed"
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    accessPin,
    selectedFile,
    selectedFields,
    session,
    validationResult?.isValid,
  ]);

  const canSubmit =
    Boolean(selectedFile) && validationResult?.isValid && !isUploading;

  const selectedFieldLabels = useMemo(
    () =>
      SUPPORT_BULK_UPDATE_FIELDS.filter((field) =>
        selectedFields.includes(field.key)
      ).map((field) => field.label),
    [selectedFields]
  );

  if (!isAccessGranted) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Paper sx={{ p: 4, width: "100%", maxWidth: 420, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h5" fontWeight={600}>
              Support Access Required
            </Typography>
            <Typography color="text.secondary">
              Enter the 4-digit support pin to access bulk student update.
            </Typography>
            <TextField
              label="Access Pin"
              type="password"
              value={accessPin}
              inputProps={{ maxLength: 4 }}
              onChange={(event) =>
                setAccessPin(event.target.value.replace(/\D/g, "").slice(0, 4))
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") handleVerifyAccess();
              }}
              fullWidth
            />
            <LoadingButton
              variant="contained"
              color="secondary"
              size="large"
              loading={verifyAccessLoading}
              onClick={handleVerifyAccess}
            >
              Verify
            </LoadingButton>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 3, px: { xs: 2, md: 3 } }}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            Support Bulk Student Update
          </Typography>
          <Typography color="text.secondary">
            First select session and fields to update, then upload the Excel file
            and click Submit.
          </Typography>
        </Stack>

        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={600}>
              Step 1 — Setup
            </Typography>

            <TextField
              label="Session"
              value={session}
              onChange={(event) => {
                setSession(event.target.value);
                resetUploadState();
              }}
              helperText="Required to verify student enrollment before update"
              fullWidth
              required
            />

            <Box>
              <TextFieldLabel
                title="Select fields to update"
                style={{ fontSize: "1rem" }}
              />
              <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                Choose one or more fields before uploading the file.
              </Typography>
              <FormGroup sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                {SUPPORT_BULK_UPDATE_FIELDS.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.key)}
                        onChange={() => toggleField(field.key)}
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>
              {selectedFieldLabels.length > 0 && (
                <Chip
                  label={`${selectedFieldLabels.length} field(s) selected`}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={handleDownloadSample}
              disabled={!selectedFields.length}
              sx={{ alignSelf: "flex-start" }}
            >
              Download Sample File
            </LoadingButton>
          </Stack>
        </Paper>

        <CustomLinearProgress loading={isValidating || isUploading} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <Box sx={{ flex: "1 1 55%", minWidth: 0 }}>
            <ResultsPanel
              validationResult={validationResult}
              uploadResponse={uploadResponse}
            />
          </Box>

          <Box sx={{ flex: "1 1 45%", minWidth: 0 }}>
            <Paper sx={{ p: 2, borderRadius: 3, height: "100%" }}>
              <Stack spacing={2} sx={{ height: "100%" }}>
                <Typography variant="h6" fontWeight={600}>
                  Step 2 — Upload & Submit
                </Typography>

                {!isSetupReady ? (
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor: "#FFF7ED",
                      border: "1px solid #FDBA74",
                      textAlign: "center",
                    }}
                  >
                    <Typography color="text.secondary">
                      Complete Step 1 first — enter session and select at least
                      one field to update.
                    </Typography>
                  </Paper>
                ) : (
                  <BulkFileUploadArea
                    title="Upload Excel file for validation"
                    buttonLabel="Download Sample File"
                    sampleLoading={false}
                    uploadLoading={isValidating}
                    onDownload={handleDownloadSample}
                    onFileSelect={handleFileSelect}
                  />
                )}

                {selectedFile && (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={selectedFile.name}
                      onDelete={resetUploadState}
                      color={validationResult?.isValid ? "success" : "error"}
                      variant="outlined"
                    />
                    {validationResult?.isValid && (
                      <Chip
                        label={`${validationResult.validRowCount} rows ready`}
                        color="success"
                        size="small"
                      />
                    )}
                  </Stack>
                )}

                <Box sx={{ mt: "auto", pt: 1 }}>
                  <LoadingButton
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    loading={isUploading}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                  >
                    Submit
                  </LoadingButton>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Box>
  );
};

export default SupportBulkStudentUpdate;
